import React, { useEffect, useState, Fragment, useCallback } from 'react';
import { FormGroup, Label, Input, FormText } from 'reactstrap';
import { Select } from 'selfly-ui-react';
import ProductTabs from '../segments/productTabs';
import { allergens, dietaryBadges, nutritionAmountTypes, servingSizeUnits } from '../config';

/**
 * Parse numeric input value to number or undefined
 * @param {string|number} value - The input value
 * @returns {number|undefined} - Parsed number or undefined
 */
const parseNumericValue = (value) => {
    if (value === '' || value === null || value === undefined) return undefined;
    const parsed = parseFloat(value);
    return isNaN(parsed) ? undefined : parsed;
};

/**
 * Normalize product data to ensure numeric fields are numbers, not strings
 * @param {object} product - The product data to normalize
 * @returns {object} - Normalized product data
 */
const normalizeProductData = (product) => {
    if (!product) return product;

    const normalized = { ...product };

    // Normalize servingSize.value
    if (normalized.servingSize?.value) {
        normalized.servingSize = {
            ...normalized.servingSize,
            value: parseNumericValue(normalized.servingSize.value),
        };
    }

    // Normalize all nutrition numeric fields
    if (normalized.nutrition) {
        const numericFields = ['calories', 'protein', 'carbs', 'sugar', 'fat', 'saturatedFat', 'salt'];
        normalized.nutrition = { ...normalized.nutrition };
        numericFields.forEach(field => {
            if (normalized.nutrition[field] !== undefined) {
                normalized.nutrition[field] = parseNumericValue(normalized.nutrition[field]);
            }
        });
    }

    return normalized;
};

const IngredientsAndNutrition = ({
    selectedProduct,
    productDataChange,
    tabNavigation,
    activeTab,
    t,
    submitProductToApi,
}) => {
    const [productData, setProductData] = useState(() => normalizeProductData(selectedProduct));
    const [ingredientInput, setIngredientInput] = useState('');
    const [ingredientError, setIngredientError] = useState('');

    useEffect(() => {
        setProductData(normalizeProductData(selectedProduct));
    }, [selectedProduct]);

    /**
     * Handle changes to simple fields or nested object fields
     * @param {string} field - The field name
     * @param {any} value - The new value
     * @param {string} nested - Optional nested field name for objects
     */
    const onFieldChange = (field, value, nested = null) => {
        if (nested) {
            setProductData(prevState => ({
                ...prevState,
                [field]: {
                    ...prevState[field],
                    [nested]: value === '' ? undefined : value,
                },
            }));
        } else {
            setProductData(prevState => ({
                ...prevState,
                [field]: value,
            }));
        }
    };

    /**
     * Handle multi-select changes (for allergens and badges)
     * @param {string} field - The array field name (allergy or badge)
     * @param {Array} selectedItems - Array of selected items from Select component
     */
    const handleMultiSelectChange = (field, selectedItems) => {
        setProductData(prevState => ({
            ...prevState,
            [field]: selectedItems.map(item => item.value),
        }));
    };

    /**
     * Custom option formatter for allergen select with icons
     */
    const formatAllergenOption = useCallback(option => {
        // Use wheat icon for rye (rye.png doesn't exist)
        const iconName = option.value === 'rye' ? 'wheat' : option.value;
        const iconPath = `/allergyIcons/${iconName}.png`;
        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <img src={iconPath} alt={option.label} style={{ width: '24px', height: '24px', objectFit: 'contain' }} onError={e => (e.target.style.display = 'none')} />
                <span>{option.label}</span>
            </div>
        );
    }, []);

    /**
     * Custom option formatter for badge select with icons
     */
    const formatBadgeOption = useCallback(option => {
        // Convert camelCase to lowercase for icon filename (e.g., glutenFree -> glutenfree)
        const iconName = option.value.toLowerCase();
        const iconPath = `/badges/${iconName}.png`;
        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <img src={iconPath} alt={option.label} style={{ width: '24px', height: '24px', objectFit: 'contain' }} onError={e => (e.target.style.display = 'none')} />
                <span>{option.label}</span>
            </div>
        );
    }, []);

    /**
     * Add ingredient to the list with validation
     * Validates: empty, max length (100), duplicates, max items (50)
     */
    const addIngredient = () => {
        const trimmed = ingredientInput.trim();

        // Clear previous error
        setIngredientError('');

        // Validation 1: Empty check
        if (trimmed === '') {
            return;
        }

        // Validation 2: Max length check (100 characters)
        if (trimmed.length > 100) {
            setIngredientError('Ingredient name is too long (maximum 100 characters)');
            return;
        }

        const currentIngredients = productData.ingredients || [];

        // Validation 3: Duplicate check (case-insensitive)
        if (currentIngredients.some(item => item.toLowerCase() === trimmed.toLowerCase())) {
            setIngredientError('This ingredient has already been added');
            return;
        }

        // Validation 4: Max items check (50 ingredients)
        if (currentIngredients.length >= 50) {
            setIngredientError('Maximum 50 ingredients allowed');
            return;
        }

        // All validations passed - add ingredient
        setProductData(prevState => ({
            ...prevState,
            ingredients: [...currentIngredients, trimmed],
        }));
        setIngredientInput('');
        setIngredientError('');
    };

    /**
     * Remove ingredient from the list
     * @param {number} index - The index to remove
     */
    const removeIngredient = index => {
        setProductData(prevState => ({
            ...prevState,
            ingredients: prevState.ingredients.filter((_, i) => i !== index),
        }));
    };

    /**
     * Handle Enter key press in ingredient input
     */
    const handleIngredientKeyPress = e => {
        if (e.key === 'Enter') {
            e.preventDefault();
            addIngredient();
        }
    };

    /**
     * Send the current state of product data to global state
     * @param {number} index
     * @param {*} tabChange
     * @param {boolean} submitData
     */
    const navigateToNextTab = (index, tabChange, submitData = false) => {
        productDataChange(productData);
        if (submitData) {
            submitProductToApi(true);
        } else {
            tabNavigation(index, tabChange, false);
        }
    };

    return (
        <Fragment>
            <ProductTabs tabChange={navigateToNextTab} activeTab={activeTab} t={t} />
            <div className="row">
                <div className="col-12">
                    {/* Ingredients Section */}
                    <div className="products__group">
                        <h3 className="products__group-title">{t('ingredients')}</h3>
                        <FormGroup>
                            <Label for="ingredientInput">{t('ingredientsList')}</Label>
                            <div style={{ display: 'flex', marginBottom: '10px' }}>
                                <Input
                                    id="ingredientInput"
                                    type="text"
                                    value={ingredientInput}
                                    onChange={e => {
                                        setIngredientInput(e.target.value);
                                        if (ingredientError) setIngredientError('');
                                    }}
                                    onKeyPress={handleIngredientKeyPress}
                                    placeholder={t('typeAndPressEnter')}
                                    maxLength={100}
                                    style={{ flex: 1, marginRight: '10px' }}
                                    invalid={!!ingredientError}
                                />
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={addIngredient}
                                    disabled={!ingredientInput.trim()}
                                >
                                    {t('add')}
                                </button>
                            </div>
                            {ingredientError && (
                                <FormText color="danger" style={{ display: 'block', marginBottom: '10px' }}>
                                    {ingredientError}
                                </FormText>
                            )}
                            <div>
                                {productData.ingredients?.length > 0 ? (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                        {productData.ingredients.map((ingredient, index) => (
                                            <div
                                                key={`${ingredient}-${index}`}
                                                style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    backgroundColor: '#e0e0e0',
                                                    padding: '5px 10px',
                                                    borderRadius: '3px',
                                                }}
                                            >
                                                <span>{ingredient}</span>
                                                <button
                                                    type="button"
                                                    onClick={() => removeIngredient(index)}
                                                    style={{
                                                        marginLeft: '8px',
                                                        border: 'none',
                                                        background: 'transparent',
                                                        cursor: 'pointer',
                                                        fontSize: '16px',
                                                        lineHeight: '1',
                                                    }}
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <FormText className="products__helptext">{t('ingredientsHelpText')}</FormText>
                                )}
                            </div>
                        </FormGroup>
                    </div>

                    {/* Allergen Section */}
                    <div className="products__group">
                        <h3 className="products__group-title">{t('allergenInformation')}</h3>
                        <FormGroup>
                            <Label for="allergens">{t('selectAllergens')}</Label>
                            <Select
                                id="allergens"
                                placeholder={t('selectOrtype')}
                                isMulti
                                onChange={selectedItems => handleMultiSelectChange('allergy', selectedItems || [])}
                                value={(productData.allergy || []).map(allergen => ({
                                    label: t(`allergen:${allergen}`),
                                    value: allergen,
                                }))}
                                options={allergens.map(allergen => ({
                                    label: t(`allergen:${allergen}`),
                                    value: allergen,
                                }))}
                                formatOptionLabel={formatAllergenOption}
                            />
                        </FormGroup>
                    </div>

                    {/* Dietary Badges Section */}
                    <div className="products__group">
                        <h3 className="products__group-title">{t('dietaryBadges')}</h3>
                        <FormGroup>
                            <Label for="badges">{t('selectBadges')}</Label>
                            <Select
                                id="badges"
                                placeholder={t('selectOrtype')}
                                isMulti
                                onChange={selectedItems => handleMultiSelectChange('badge', selectedItems || [])}
                                value={(productData.badge || []).map(badge => ({
                                    label: t(`badge:${badge}`),
                                    value: badge,
                                }))}
                                options={dietaryBadges.map(badge => ({
                                    label: t(`badge:${badge}`),
                                    value: badge,
                                }))}
                                formatOptionLabel={formatBadgeOption}
                            />
                        </FormGroup>
                    </div>

                    {/* Serving Size Section */}
                    <div className="products__group">
                        <h3 className="products__group-title">{t('servingSize')}</h3>
                        <div className="row">
                            <FormGroup className="col-md-6">
                                <Label for="servingValue">{t('value')}</Label>
                                <Input
                                    id="servingValue"
                                    type="number"
                                    min="0"
                                    step="0.1"
                                    value={productData.servingSize?.value || ''}
                                    onChange={e => onFieldChange('servingSize', parseNumericValue(e.target.value), 'value')}
                                />
                            </FormGroup>
                            <FormGroup className="col-md-6">
                                <Label for="servingUnit">{t('unit')}</Label>
                                <Input
                                    id="servingUnit"
                                    type="select"
                                    value={productData.servingSize?.unit || 'g'}
                                    onChange={e => onFieldChange('servingSize', e.target.value, 'unit')}
                                >
                                    {servingSizeUnits.map(unit => (
                                        <option key={unit.value} value={unit.value}>
                                            {unit.label}
                                        </option>
                                    ))}
                                </Input>
                            </FormGroup>
                        </div>
                    </div>

                    {/* Nutrition Facts Section */}
                    <div className="products__group">
                        <h3 className="products__group-title">{t('nutritionFacts')}</h3>

                        <FormGroup>
                            <Label for="amountType">{t('nutritionAmountType')}</Label>
                            <Input
                                id="amountType"
                                type="select"
                                value={productData.nutrition?.amountType || 'per100grams'}
                                onChange={e => onFieldChange('nutrition', e.target.value, 'amountType')}
                            >
                                {nutritionAmountTypes.map(type => (
                                    <option key={type.value} value={type.value}>
                                        {type.label}
                                    </option>
                                ))}
                            </Input>
                        </FormGroup>

                        <div className="row">
                            <FormGroup className="col-md-6">
                                <Label for="calories">{t('calories')} (kcal)</Label>
                                <Input
                                    id="calories"
                                    type="number"
                                    min="0"
                                    step="1"
                                    value={productData.nutrition?.calories || ''}
                                    onChange={e => onFieldChange('nutrition', parseNumericValue(e.target.value), 'calories')}
                                />
                            </FormGroup>
                            <FormGroup className="col-md-6">
                                <Label for="protein">{t('protein')} (g)</Label>
                                <Input
                                    id="protein"
                                    type="number"
                                    min="0"
                                    step="0.1"
                                    value={productData.nutrition?.protein || ''}
                                    onChange={e => onFieldChange('nutrition', parseNumericValue(e.target.value), 'protein')}
                                />
                            </FormGroup>
                        </div>

                        <div className="row">
                            <FormGroup className="col-md-6">
                                <Label for="carbs">{t('carbohydrates')} (g)</Label>
                                <Input
                                    id="carbs"
                                    type="number"
                                    min="0"
                                    step="0.1"
                                    value={productData.nutrition?.carbs || ''}
                                    onChange={e => onFieldChange('nutrition', parseNumericValue(e.target.value), 'carbs')}
                                />
                            </FormGroup>
                            <FormGroup className="col-md-6">
                                <Label for="sugar">{t('sugar')} (g)</Label>
                                <Input
                                    id="sugar"
                                    type="number"
                                    min="0"
                                    step="0.1"
                                    value={productData.nutrition?.sugar || ''}
                                    onChange={e => onFieldChange('nutrition', parseNumericValue(e.target.value), 'sugar')}
                                />
                            </FormGroup>
                        </div>

                        <div className="row">
                            <FormGroup className="col-md-6">
                                <Label for="fat">{t('fat')} (g)</Label>
                                <Input
                                    id="fat"
                                    type="number"
                                    min="0"
                                    step="0.1"
                                    value={productData.nutrition?.fat || ''}
                                    onChange={e => onFieldChange('nutrition', parseNumericValue(e.target.value), 'fat')}
                                />
                            </FormGroup>
                            <FormGroup className="col-md-6">
                                <Label for="saturatedFat">{t('saturatedFat')} (g)</Label>
                                <Input
                                    id="saturatedFat"
                                    type="number"
                                    min="0"
                                    step="0.1"
                                    value={productData.nutrition?.saturatedFat || ''}
                                    onChange={e => onFieldChange('nutrition', parseNumericValue(e.target.value), 'saturatedFat')}
                                />
                            </FormGroup>
                        </div>

                        <div className="row">
                            <FormGroup className="col-md-6">
                                <Label for="salt">{t('salt')} (g)</Label>
                                <Input
                                    id="salt"
                                    type="number"
                                    min="0"
                                    step="0.1"
                                    value={productData.nutrition?.salt || ''}
                                    onChange={e => onFieldChange('nutrition', parseNumericValue(e.target.value), 'salt')}
                                />
                            </FormGroup>
                        </div>
                    </div>
                </div>
            </div>

            {/* Navigation Buttons */}
            <div className="row">
                <div className="col">
                    <div className="products__cta-buttons">
                        <div className="products__cta-button products__cta-button--neutral" onClick={() => navigateToNextTab(-1)}>
                            ‹‹ {t('previous')}
                        </div>
                        <div className="products__cta-button products__cta-button--success" onClick={() => navigateToNextTab(1, null, true)}>
                            {t('save')}
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

export default IngredientsAndNutrition;
